import IndustryLandingTemplate, { IndustryPageData } from '@/components/industry/IndustryLandingTemplate';

const healthcareData: IndustryPageData = {
  slug: 'healthcare',
  name: 'Healthcare & Clinics',
  heroHeadline: 'Turn Patient Satisfaction into 5-Star Google Reviews',
  heroSubheadline: 'Help your clinic, hospital, or healthcare practice build trust with AI-generated review suggestions that patients actually want to post.',
  painPoint: '78% of patients choose healthcare providers based on Google reviews',
  trustProblem: 'Yet most clinics struggle to collect authentic feedback from satisfied patients.',
  challenges: [
    {
      title: 'Low Google Review Count',
      description: 'Patients visit, get treated, and leave without thinking about leaving a review. Your competitors with more reviews rank higher.',
    },
    {
      title: 'Poor Google Maps Visibility',
      description: 'Without reviews containing location keywords, your clinic doesn\'t show up in "doctor near me" or "clinic in [city]" searches.',
    },
    {
      title: 'Generic Patient Feedback',
      description: 'When patients do leave reviews, they\'re often vague like "good doctor" without mentioning specific services or treatments.',
    },
    {
      title: 'Missing Service Keywords',
      description: 'Reviews don\'t mention specific treatments (dental implants, skin treatment, physiotherapy) that potential patients search for.',
    },
  ],
  useCases: [
    {
      category: 'General Consultation',
      description: 'Reviews highlighting doctor expertise, clinic hygiene, and staff behavior.',
      keywords: ['experienced doctor', 'clean clinic', 'friendly staff'],
    },
    {
      category: 'Specialized Treatments',
      description: 'Service-specific reviews for dental, dermatology, orthopedics, and more.',
      keywords: ['root canal treatment', 'skin specialist', 'knee surgery'],
    },
    {
      category: 'Location-Based',
      description: 'Reviews that boost local SEO with city and area mentions.',
      keywords: ['best clinic in Mumbai', 'doctor in Andheri', 'hospital near me'],
    },
  ],
  caseStudy: {
    businessType: 'Multi-Specialty Clinic',
    city: 'Bengaluru',
    challenges: [
      'Only 23 Google reviews despite 5+ years of operation',
      'Not appearing in "clinic near me" searches in Koramangala',
      'Competitor clinics ranking higher with more reviews',
    ],
    solution: 'Implemented Review Plum with category-based review generation for General Medicine, Pediatrics, and Gynecology. Created service-specific templates with Bengaluru locality keywords.',
    results: [
      { metric: 'Google Reviews', value: '23 → 127' },
      { metric: 'Google Rating', value: '4.1 → 4.6' },
      { metric: 'Map Pack Appearances', value: '+340%' },
      { metric: 'New Patient Inquiries', value: '+65%' },
    ],
  },
  sampleReviews: [
    {
      category: 'General Consultation',
      reviews: [
        {
          text: 'Visited the clinic for a routine checkup and was impressed by the thorough examination. Dr. Sharma took time to explain everything clearly. The clinic in Koramangala is very accessible and the staff was courteous. Highly recommend for anyone looking for a reliable doctor in Bengaluru.',
          keywords: ['clinic in Koramangala', 'doctor in Bengaluru', 'routine checkup'],
        },
        {
          text: 'Best healthcare experience I\'ve had in years. The multi-specialty clinic offers everything under one roof. From registration to consultation, the process was smooth. Will definitely recommend to friends and family looking for quality healthcare near HSR Layout.',
          keywords: ['multi-specialty clinic', 'healthcare near HSR Layout', 'quality healthcare'],
        },
      ],
    },
    {
      category: 'Dental Care',
      reviews: [
        {
          text: 'Got my root canal treatment done here and it was completely painless! The dentist in Indiranagar was extremely skilled and explained every step of the procedure. The clinic is well-equipped with modern dental equipment. Highly recommend for any dental treatment in Bengaluru.',
          keywords: ['root canal treatment', 'dentist in Indiranagar', 'dental treatment Bengaluru'],
        },
        {
          text: 'Best dental clinic for teeth whitening and scaling. My smile looks amazing now! The dental hygienist was thorough and gentle. Very reasonable prices for the quality of service. Top dental care in East Bengaluru.',
          keywords: ['teeth whitening', 'dental clinic', 'dental care East Bengaluru'],
        },
      ],
    },
    {
      category: 'Pediatrics',
      reviews: [
        {
          text: 'Dr. Patel is the best pediatrician in Whitefield! My daughter was very comfortable during the checkup. The doctor took time to answer all my questions about vaccinations. The children\'s play area in the clinic is a great touch. Highly recommend for parents in Bengaluru.',
          keywords: ['pediatrician in Whitefield', 'child specialist', 'vaccination Bengaluru'],
        },
        {
          text: 'Took my son for fever and cold treatment. The pediatric care here is exceptional. The child-friendly environment made my son feel at ease. Quick diagnosis and effective treatment. Best child doctor near ITPL.',
          keywords: ['pediatric care', 'child doctor near ITPL', 'fever treatment'],
        },
      ],
    },
  ],
  cityKeywords: [
    'clinic in Mumbai',
    'doctor in Delhi',
    'hospital Bengaluru',
    'healthcare Chennai',
    'specialist Hyderabad',
    'medical center Pune',
  ],
  nearMeKeywords: [
    'doctor near me',
    'clinic near me',
    'hospital near me',
    'best doctor nearby',
    'healthcare center close by',
    '24 hour clinic near me',
  ],
  ctaText: 'Get More Patient Reviews',
};

export default function HealthcarePage() {
  return <IndustryLandingTemplate data={healthcareData} />;
}
